import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import axios from 'axios';
import { UpdateUserPayload } from 'lib/api/schemas/user-mgmt';
import { useSnackbar } from 'notistack';

import { useApiClient } from '@/lib/api';
import {
  AuthResourcesPaginationOptions,
  InviteUserPayload,
  UserOrgMemberPayload,
} from '@/lib/api/schemas/user-management';

import { useAuth } from '../auth/useAuth';

export const useGetCurrentUser = () => {
  const {
    permissions,
    allowedToEditAndCreate,
    accessToken,
    user,
    allowedLocationIds,
    isLoading,
    orgName,
  } = useAuth();

  return {
    scope: permissions,
    allowedToEditAndCreate,
    accessToken,
    user,
    isLoading,
    orgName,
    locationIds: allowedLocationIds,
  };
};

export const useGetAuthLogs = () => {
  const { userId } = useAuth();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['org-management', userId, 'logs'],
    enabled: !!userId,
    queryFn: async () => {
      return await apiClient.getCurrentUserAuthLogs();
    },
    select: (data) => {
      return {
        ...data,
        logs: data.logs
          .filter(
            (log) =>
              log.type === 's' &&
              log.client_id === import.meta.env.VITE_AUTH0_CLIENT_ID
          )
          .sort(
            (a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf()
          ),
      };
    },
  });
};

export const useUpdateCurrentUser = (options = {}) => {
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: UpdateUserPayload }) => {
      return apiClient.updateCurrentUser(data);
    },
    onSuccess: async () => {
      await queryClient
        .invalidateQueries({
          queryKey: ['idTokenClaims'],
        })
        .then(() => {
          enqueueSnackbar('Your profile was updated successfully.', {
            variant: 'success',
          });
        });
    },
    onError: (error) => {
      if (!axios.isAxiosError(error)) {
        return;
      }
      if (
        !error.response?.data.error ||
        typeof error.response?.data.error !== 'string'
      ) {
        enqueueSnackbar('Failed to update your profile.', { variant: 'error' });
      }
    },
    ...options,
  });
};

export const useGetOrganizationMembers = (
  options: AuthResourcesPaginationOptions
) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['organization-members', options],
    queryFn: async () => {
      const orgMembers = await apiClient.getOrgMembers(options);
      // TODO: figure out how to paginate here
      const roleList = await apiClient.getAllRoles();

      return {
        ...orgMembers,
        members: orgMembers.members.map((member) => {
          return {
            ...member,
            roles: member.roles?.map((role) => ({
              ...role,
              description: roleList?.roles.find((r) => r.id === role.id)
                ?.description,
            })),
          };
        }),
      };
    },
    staleTime: 1000 * 60 * 5,
  });
};
export const useDeleteUser = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => {
      return apiClient.deleteUser(userId);
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: ['organization-members'],
      });
    },
  });
};

export const useGetAllRoles = () => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => {
      return apiClient.getAllRoles();
    },
  });
};

export const useGetUser = (
  userId?: string,
  options: { shouldHandleForbiddenInline?: boolean } = {}
) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['users', userId],
    queryFn: () => {
      return apiClient.getUser(userId!);
    },
    enabled: !!userId,
    // Per observer, so a page-owning observer of the same key still reaches
    // the route-level ForbiddenBoundary. Spread conditionally: options are
    // merged over the provider defaults, so even a `throwOnError: undefined`
    // key would replace the global forbidden predicate.
    ...(options.shouldHandleForbiddenInline ? { throwOnError: false } : {}),
  });
};

export const useUpdateOrgMember = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: UserOrgMemberPayload;
    }) => {
      const promises = [apiClient.updateUserPayload(userId, data)];

      if (data.rolesToAssign.length) {
        promises.push(
          apiClient.assignRolesToOrgMember(userId, data.rolesToAssign)
        );
      }
      if (data.rolesToDelete.length) {
        promises.push(
          apiClient.deleteRolesFromOrgMember(userId, data.rolesToDelete)
        );
      }

      return Promise.all(promises);
    },
    onSuccess: () => {
      const promises: Promise<unknown>[] = [
        queryClient.invalidateQueries({ queryKey: ['organization-members'] }),
        queryClient.invalidateQueries({ queryKey: ['users'] }),
        queryClient.invalidateQueries({
          queryKey: ['idTokenClaims'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['sublocations'],
        }),
      ];
      return Promise.all(promises);
    },
  });
};

export const useGetInvitations = (options: AuthResourcesPaginationOptions) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['invitations', options],
    queryFn: () => {
      return apiClient.getInvitations(options);
    },
    placeholderData: keepPreviousData,
  });
};

export const useInviteUser = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InviteUserPayload) => {
      return apiClient.inviteUser(data);
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: ['invitations'],
      });
    },
  });
};

export const useRevokeInvitation = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      return apiClient.deleteInvitation(id);
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: ['invitations'],
      });
    },
  });
};
